import { useCallback, useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { getResolvedWorkspace } from "../providers/supabase/workspaceConfig";

/**
 * Settings → Tracking code (round 49, decision #44): everything a client
 * needs to put the attribution script on their site - the snippet, plain
 * and Tag Manager install steps, a live "is it working" check, and
 * suggested privacy-policy language flagged clearly as not legal advice.
 */

const CopyBlock = ({ text, label }: { text: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="rounded-md border border-border bg-muted/40 p-3 pr-12 text-xs whitespace-pre-wrap break-all font-mono">
        {text}
      </pre>
      <Button
        variant="outline"
        size="sm"
        className="absolute top-2 right-2 h-7 px-2"
        aria-label={`Copy ${label}`}
        onClick={() => {
          void navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        }}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
};

export const TrackingPage = () => {
  const workspace = getResolvedWorkspace();
  const servicesUrl = (import.meta.env.VITE_CONTROL_PLANE_URL ?? "").replace(/\/+$/, "");
  const slug =
    workspace?.slug ??
    (window.location.hostname.endsWith(".obviouscrm.com")
      ? window.location.hostname.split(".")[0]
      : undefined);

  const [lastBeacon, setLastBeacon] = useState<string | null | "loading">("loading");

  const checkBeacon = useCallback(async () => {
    try {
      const { data, error } = await getSupabaseClient()
        .from("activity_events")
        .select("occurred_at")
        .eq("type", "touch.captured")
        .order("occurred_at", { ascending: false })
        .limit(1);
      if (error) throw new Error(error.message);
      setLastBeacon(data?.[0]?.occurred_at ?? null);
    } catch {
      setLastBeacon(null);
    }
  }, []);

  useEffect(() => {
    void checkBeacon();
  }, [checkBeacon]);

  if (!slug || !servicesUrl) {
    return (
      <p className="text-sm text-muted-foreground max-w-prose">
        The tracking snippet is generated per workspace and is available in
        deployed workspaces only - the demo preview has no site of its own to
        track.
      </p>
    );
  }

  const snippet = `<script async src="${servicesUrl}/t/${slug}/slate.js"></script>`;
  const privacyCopy = `We use a first-party analytics cookie to understand how visitors find and use our website. It records the pages you visit on this site, the source that brought you here (for example a search engine or campaign link), and a random identifier - it does not collect your name or contact details unless you submit them through a form. This information helps us improve our website and respond to inquiries. You can decline this cookie via our cookie settings, and you may request access to or deletion of your data by contacting us.`;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="p-4 flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Your tracking snippet</h3>
          <p className="text-xs text-muted-foreground max-w-prose">
            One line, on every page of the website you want tracked. It
            assigns visitors an anonymous first-party identifier, records how
            they arrived (search, campaign, referral), and connects that
            journey to a contact the moment they submit one of your forms -
            including visits from before they identified themselves.
          </p>
          <CopyBlock text={snippet} label="tracking snippet" />
          <p className="text-xs">
            {lastBeacon === "loading" ? (
              <span className="text-muted-foreground">Checking for activity…</span>
            ) : lastBeacon ? (
              <span className="text-[#5d8a54]">
                Working - last visit captured{" "}
                {new Date(lastBeacon).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
                .
              </span>
            ) : (
              <span className="text-muted-foreground">
                No visits captured yet - install the snippet, open your site,
                then{" "}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => void checkBeacon()}
                >
                  check again
                </button>
                .
              </span>
            )}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          <div>
            <h3 className="text-sm font-semibold mb-1">Install on a regular website</h3>
            <p className="text-xs text-muted-foreground max-w-prose">
              Paste the snippet just before the closing <code className="font-mono">&lt;/head&gt;</code>{" "}
              tag in your site's template so it loads on every page. On
              WordPress, use your theme's header template or a header-scripts
              plugin; on Webflow, Site settings → Custom code → Head code;
              on Squarespace, Settings → Advanced → Code injection → Header.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-1">Install with Google Tag Manager</h3>
            <p className="text-xs text-muted-foreground max-w-prose">
              In Tag Manager: Tags → New → Custom HTML. Paste the snippet as
              the tag's HTML, set the trigger to All Pages (Page View), name
              it "Obvious tracking", then Save and Publish the container.
              If you use GTM's consent mode, set the tag's consent settings
              to require analytics consent and the script will simply not
              run for visitors who decline.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-1">Respecting consent</h3>
            <p className="text-xs text-muted-foreground max-w-prose">
              If your site has its own cookie banner, set{" "}
              <code className="font-mono">window.slateConsent = false</code>{" "}
              before the snippet for visitors who decline - the script then
              does nothing at all: no cookie, no requests.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Suggested privacy policy language</h3>
          <p className="text-xs text-muted-foreground max-w-prose">
            Most privacy policies should disclose first-party analytics.
            Here is a starting paragraph you can adapt:
          </p>
          <CopyBlock text={privacyCopy} label="privacy policy paragraph" />
          <p className="text-xs text-muted-foreground max-w-prose">
            This is a template to discuss with your own counsel, not legal
            advice - disclosure requirements vary by state and country, and
            your policy should reflect everything your site actually does.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
